import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@/api/client'
import { useAsync } from '@/components/useAsync'
import { EvidencePicker } from './EvidencePicker'
import { RolloutPlayer } from './RolloutPlayer'
import { RubricResults, criterionRows } from '@/components/CriteriaTable'
import { useRubricLink } from './rubricLinks'
import type { Attempt, CanonicalEvent, EpisodeSummary } from '@/lib/runs'
import type { ImplementedRubricSpec } from '@/lib/types'
interface ReviewAttempt extends Attempt {
  attempt_kind?: "model" | "expert" | "reference"
  expert_error?: { phase: string; message: string; hint: string; evidence_location?: string }
  original_summary?: EpisodeSummary
  rubric_map?: ImplementedRubricSpec
  regrade?: { candidate: { commit: string }; result_sha256: string; original_outcome_sha256: string }

  model?: string
  job_id: string
  attempt_id: string
}
interface Experiment {
  run_id: string
  name: string
  model: string
  revision: string
  rubric_map?: ImplementedRubricSpec | null
  source?: string
  geometry_note?: string
  attempts: ReviewAttempt[]
}
export interface Evidence {
  attempt: ReviewAttempt
  events: CanonicalEvent[]
  next?: number
  expert_videos?: { filename: string; url: string }[]
  expert_reference?: ExpertReference | null
  graded_files?: GradedFile[]
  final_screenshot_url?: string | null
  video_url?: string
  files: { path: string; url: string }[]
}
/** The human solution's ReferenceSolution grade of the submitted package, and what was graded. */
interface ExpertReference {
  reward: number
  graded_files?: { filename: string; sha256: string; size_bytes: number }[]
  expert_original?: { file_id: string; filename: string; kind: string } | null
  provenance_error?: string
}
/** One file the ReferenceSolution grade used; url is the frozen byte copy, when the run kept one. */
interface GradedFile {
  filename: string
  /** The document the program opened and graded; the other files were provided alongside it. */
  opened?: boolean
  sha256?: string
  size_bytes?: number
  url?: string | null
}
/** The latest failed grading of the expert's solution against the final package. */
interface ExpertGradingFailure {
  attempt_kind: 'expert' | 'reference'
  started_at?: string
  phase?: string | null
  code?: string | null
  error_class?: string | null
  message?: string | null
  hint?: string | null
  files?: string[]
}
const FAILURE_KEY = 'expert-grading-failure'
const isExpert = (kind?: string) => kind === 'expert' || kind === 'reference'
interface SupplementalArtifact {
  entry_id: string
  run_id: string
  job_id: string
  attempt_id: string
  revision: string
  model: string
  reward: number | null
  classification: string
  verifier_status: string | null
  path: string
  size: number
}
interface RolloutList {
  experiments: Experiment[]
  /** Why no graded expert submission is listed; absent when one is. */
  expert_notice?: { kind: string; message: string; failure?: ExpertGradingFailure } | null
  rubric_map?: ImplementedRubricSpec | null
  supplemental_artifacts?: SupplementalArtifact[]
}
export interface RolloutExamples extends RolloutList {
  selectedKey?: string
  evidence: Record<string, Evidence>
}

export function ReviewRollouts({
  taskId,
  roundId,
  active = true,
  example,
}: {
  /** Browser-only records; never load artifact URLs from task storage. */
  example?: RolloutExamples
  active?: boolean
  taskId: string
  roundId: string
}) {
  const base = `/tasks/${taskId}/reviews/${roundId}/experiments`
  const { data, error, loading } = useAsync<RolloutList>(
    () => example ? Promise.resolve(example) : api.get<RolloutList>(base),
    [base, example],
  )
  const [selectedKey, setSelectedKey] = useState(example?.selectedKey ?? '')
  if (loading) return <p className="muted">Loading rollouts…</p>
  if (!data) return <p className="note err">{error}</p>
  const attempts = data.experiments.flatMap((run) =>
    run.attempts.map((attempt) => ({
      run,
      attempt,
      key: `${run.run_id}/${attempt.job_id}/${attempt.attempt_id}`,
    })),
  )
  const failure = data.expert_notice?.kind === 'failed' ? data.expert_notice : null
  const showingFailure = failure != null && (selectedKey === FAILURE_KEY || attempts.length === 0)
  const selected = attempts.find((item) => item.key === selectedKey) ?? attempts[0]
  return (
    <section className="box review-rollouts">
      <div className="panel-section-head">
        <h3>Rollouts</h3>
        <div className="row">
          {attempts.length + (failure ? 1 : 0) > 1 && selected && (
            <EvidencePicker
              label="Rollout"
              value={showingFailure ? FAILURE_KEY : selected.key}
              onChange={setSelectedKey}
              options={[...attempts.map((item, index) => ({
                value: item.key,
                label: isExpert(item.attempt.attempt_kind) ? "Expert submission" : `Rollout ${index + 1}`,
                detail: `${item.attempt.regrade ? 'Regraded · ' : item.run.source === 'backfill' ? 'Backfilled · ' : ''}${isExpert(item.attempt.attempt_kind) ? 'Expert submission' : item.attempt.model || item.run.model || 'Unknown model'} · Reward ${item.attempt.summary?.reward == null ? 'not scored' : item.attempt.summary.reward.toFixed(2)}`,
              })), ...(failure ? [{ value: FAILURE_KEY, label: 'Expert submission', detail: 'Expert submission · Grading failed' }] : [])]}
            />
          )}
          {selected && !showingFailure && (
            <span className="chip">
              Reward{' '}
              {selected.attempt.summary?.reward == null
                ? 'not scored'
                : selected.attempt.summary.reward.toFixed(2)}
            </span>
          )}
        </div>
      </div>
      {data.expert_notice && !failure && <p className="note small" role="status">{data.expert_notice.message}</p>}
      {failure?.failure && showingFailure ? <ExpertFailureCard message={failure.message} failure={failure.failure} /> : !selected ? (
        <p className="muted">No rollout evidence was linked to this submission.</p>
      ) : (
        <AttemptView example={example?.evidence[selected.key]} key={selected.key} base={`${base}/${selected.key}`} active={active}
                     rubricMap={selected.run.rubric_map === undefined ? data.rubric_map : selected.run.rubric_map} taskId={taskId} roundId={roundId}
                     geometryNote={selected.run.geometry_note}
                     supplementalArtifacts={(data.supplemental_artifacts ?? []).filter((item) =>
                       item.run_id === selected.run.run_id && item.job_id === selected.attempt.job_id
                       && item.attempt_id === selected.attempt.attempt_id)} />
      )}
    </section>
  )
}

function AttemptView({ base, active, rubricMap, taskId, roundId, supplementalArtifacts, geometryNote, example }: {
  example?: Evidence
  base: string
  active: boolean
  rubricMap?: ImplementedRubricSpec | null
  taskId: string
  roundId: string
  supplementalArtifacts: SupplementalArtifact[]
  geometryNote?: string
}) {
  const { data, setData, error, loading } = useAsync(() => example ? Promise.resolve(example) : api.get<Evidence>(base), [base, example])
  const [loadingMore, setLoadingMore] = useState(false)
  const [failure, setFailure] = useState('')
  const refreshedVideos = useRef(new Set<string>())
  const refreshing = useRef(false)
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const refreshLinks = useCallback(async () => {
    if (example || refreshing.current) return
    refreshing.current = true
    try {
      const fresh = await api.get<Evidence>(base)
      let cursor = fresh.next
      while (cursor != null && mounted.current) {
        const page = await api.get<Evidence>(base, { after: cursor })
        fresh.events.push(...page.events)
        cursor = page.next
      }
      if (mounted.current) { setData({ ...fresh, next: cursor }); setFailure('') }
    } catch (error) {
      if (mounted.current) setFailure(`Could not refresh evidence: ${(error as Error).message}`)
    } finally { refreshing.current = false }
  }, [base, setData, example])
  async function openFile(path: string) {
    if (example) return
    // Open during the click, then obtain a fresh URL without triggering popup blocking.
    const tab = window.open('', '_blank')
    if (tab) tab.opener = null
    try {
      const fresh = await api.get<Evidence>(base)
      const file = fresh.files.find((item) => item.path === path)
      if (!file) throw new Error('This file is no longer available.')
      if (tab) tab.location.replace(file.url)
    } catch (error) { tab?.close(); setFailure((error as Error).message) }
  }
  async function openSupplementalArtifact(entryId: string) {
    if (example) return
    const tab = window.open('', '_blank')
    if (tab) tab.opener = null
    try {
      const result = await api.get<{ url: string }>(`/tasks/${taskId}/reviews/${roundId}/supplemental-artifacts/${entryId}`)
      if (tab) tab.location.replace(result.url)
    } catch (error) { tab?.close(); setFailure((error as Error).message) }
  }
  // Load the full frozen timeline so playback never silently stops following at a page boundary.
  useEffect(() => {
    if (example || data?.next == null || failure) return
    let cancelled = false
    setLoadingMore(true)
    api
      .get<Evidence>(base, { after: data.next })
      .then((next) => {
        if (!cancelled)
          setData((current) =>
            current ? { ...next, events: [...current.events, ...next.events] } : next,
          )
      })
      .catch((error) => {
        if (!cancelled) setFailure((error as Error).message)
      })
      .finally(() => {
        if (!cancelled) setLoadingMore(false)
      })
    return () => {
      cancelled = true
    }
  }, [base, data?.next, failure, setData, example])
  const [showOriginal, setShowOriginal] = useState(false)
  // The map this grade was computed with: a regrade carries its own.
  const gradedWith = data && !showOriginal && data.attempt.regrade ? data.attempt.rubric_map : rubricMap
  const openCheck = useRubricLink(gradedWith)
  if (loading && !data) return <p>Loading evidence…</p>
  if (!data) return <p className="note err">{error}</p>
  const summary = showOriginal ? data.attempt.original_summary : data.attempt.summary
  // The package's published map decides which checks exist; this attempt only says
  // how far it got through them.
  const rows = criterionRows(summary?.verifier?.criteria, gradedWith)
  const extraFiles = supplementalArtifacts.filter((item) => !data.files.some((file) => file.path === item.path))
  return (
    <div className="review-attempt" data-tour={example ? 'example-quality' : undefined}>
      {geometryNote && <p className="note warn small">{geometryNote}</p>}
      {isExpert(data.attempt.attempt_kind) ? (
        <div className="expert-submission-video">
          {data.attempt.classification === "pending" && <p role="status">Expert grading is pending.</p>}
          {data.final_screenshot_url
            ? <figure>
                <img src={data.final_screenshot_url} alt="The expert's design as loaded and graded"
                     style={{ width: '100%', maxHeight: '65vh', objectFit: 'contain' }}
                     onError={(event) => {
                       // Presigned links expire; refresh once, then leave the broken image.
                       if (!event.currentTarget.dataset.refreshed) { event.currentTarget.dataset.refreshed = '1'; void refreshLinks() }
                     }} />
                <figcaption className="muted small">The expert’s design as loaded and graded, without model actions.</figcaption>
              </figure>
            : <p className="muted">No screenshot of the graded design was captured.</p>}
          <GradedFiles files={data.graded_files ?? []} />
          {data.expert_reference
            ? <ReferenceProvenance reference={data.expert_reference} />
            : <p className="muted small">The expert’s submitted final document is loaded into a rollout machine, then immediately exported and graded without model actions.</p>}
          {data.attempt.expert_error && <div className="note err" role="alert">
            <strong>Expert grading failed: {data.attempt.expert_error.phase}</strong>
            <p>{data.attempt.expert_error.message}</p><p>{data.attempt.expert_error.hint}</p>
          </div>}
          {(data.expert_videos ?? []).length > 0 && <details>
            <summary>The expert’s screen recordings ({data.expert_videos!.length})</summary>
            {data.expert_videos!.map((clip) => (
              <figure key={clip.filename}>
                <video controls preload="metadata" src={clip.url} onError={() => {
                         if (!refreshedVideos.current.has(clip.filename)) {
                           refreshedVideos.current.add(clip.filename); void refreshLinks()
                         } else setFailure("This video could not play. Open the original file using its link below.")
                       }}
                       style={{ width: "100%", maxHeight: "65vh" }} />
                <figcaption><a href={clip.url} target="_blank" rel="noreferrer">{clip.filename}</a></figcaption>
              </figure>
            ))}
          </details>}
        </div>
      ) : <RolloutPlayer
        events={data.events}
        videoUrl={data.video_url}
        active={active}
        onStaleImage={example ? undefined : refreshLinks}
        hasMore={data.next != null}
        loadingMore={loadingMore}
        loadMore={() => setFailure('')}
      />}
      {data.attempt.regrade && <div className="note small">
        <strong>Regraded</strong> · package <code>{data.attempt.regrade.candidate.commit}</code>
        <p>Original reward: {data.attempt.original_summary?.reward ?? 'unscored'} · Regraded reward: {data.attempt.summary?.reward ?? 'unscored'}</p>
        <button type="button" className="btn small" onClick={() => setShowOriginal(!showOriginal)}>
          {showOriginal ? 'Show regraded result' : 'Show original grade'}
        </button>
        <details><summary>Grade provenance</summary><p>Result SHA-256: {data.attempt.regrade.result_sha256}</p>
          <p>Original outcome SHA-256: {data.attempt.regrade.original_outcome_sha256}</p></details>
      </div>}
      <RubricResults rows={rows} onOpenCheck={openCheck} />
      {(data.files.length > 0 || extraFiles.length > 0) && (
        <details className="rollout-details">
          <summary>Captured evidence ({data.files.length + extraFiles.length})</summary>
          <ul>
            {data.files.map((file) => (
              <li key={file.path}>
                <a href={file.url} target="_blank" rel="noreferrer" onClick={(event) => { event.preventDefault(); void openFile(file.path) }}>
                  {file.path}
                </a>
              </li>
            ))}
            {extraFiles.map((item) => (
              <li key={item.entry_id}>
                <a href="#" onClick={(event) => { event.preventDefault(); void openSupplementalArtifact(item.entry_id) }}>
                  {item.path}
                </a>
                <span className="muted small"> · supplemental from {item.model} rollout · {item.reward === null ? 'unscored' : `reward ${item.reward.toFixed(2)}`}
                  {item.verifier_status === 'error' ? ' · verifier error' : ''}
                  {' · '}run {item.run_id}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {data.attempt.error_class && <p className="note warn small">{data.attempt.error_class}</p>}

      {failure && <p className="note err">{failure}</p>}
    </div>
  )
}

function ExpertFailureCard({ message, failure }: { message: string; failure: ExpertGradingFailure }) {
  return <div className="review-attempt">
    <div className="note err" role="alert">
      <strong>Grading the expert’s solution failed</strong>
      <p>{message}</p>
      {failure.hint && <p>{failure.hint}</p>}
      <p className="small">
        {failure.attempt_kind === 'reference' ? 'ReferenceSolution grade' : 'Expert grading run'}
        {failure.phase && <> · phase <code>{failure.phase}</code></>}
        {failure.code && <> · <code>{failure.code}</code></>}
        {failure.error_class && <> · <code>{failure.error_class}</code></>}
        {failure.started_at && <> · {new Date(failure.started_at).toLocaleString()}</>}
      </p>
      {failure.files && failure.files.length > 0 && <p className="small">Expert file{failure.files.length === 1 ? '' : 's'}: {failure.files.join(', ')}</p>}
    </div>
    <p className="muted small">The reviewers judge the verifier without an expert grade on this package.</p>
  </div>
}

function GradedFiles({ files }: { files: GradedFile[] }) {
  const opened = files.find(file => file.opened)
  const others = files.filter(file => !file.opened)
  const link = (file: GradedFile) => <>
    {file.url
      ? <a href={file.url} target="_blank" rel="noreferrer">{file.filename}</a>
      : <span>{file.filename} <span className="muted">(the run kept no copy of this file)</span></span>}
    <span className="muted small">
      {file.size_bytes != null && ` · ${(file.size_bytes / 1024 / 1024).toFixed(1)} MB`}
      {file.sha256 && <> · SHA-256 <code title={file.sha256}>{file.sha256.slice(0, 12)}…</code></>}
    </span>
  </>
  if (!files.length) return <p className="muted small">The graded file was not recorded for this attempt.</p>
  return <div className="graded-files small">
    {opened && <p><strong>Graded file:</strong> {link(opened)}</p>}
    {others.length > 0 && <p>{opened ? 'Also provided' : 'Provided files'}: {others.map((file, index) =>
      <span key={file.filename}>{index > 0 && ', '}{link(file)}</span>)}</p>}
  </div>
}

function ReferenceProvenance({ reference }: { reference?: ExpertReference | null }) {
  const graded = reference?.graded_files?.map(file => file.filename).join(', ')
  return <div className="small">
    <p className="muted">The authoring agent graded the expert’s solution with ReferenceSolution against this exact package, without model actions.</p>
    {reference?.expert_original
      ? <p>Graded file: the expert’s original upload <strong>{reference.expert_original.filename}</strong>.</p>
      : reference?.provenance_error
        ? <p className="note warn">The graded file could not be compared with the expert’s uploads: {reference.provenance_error}</p>
        : <p className="note warn">The graded file{graded ? ` (${graded})` : ''} is not identical to any expert upload. The authoring agent supplied or converted it.</p>}
  </div>
}
